import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Language = 'en' | 'ar' | 'fr';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  locale: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'tenjah_language';
  private currentLanguageSubject: BehaviorSubject<Language>;
  public currentLanguage$: Observable<Language>;
  private isBrowser: boolean;

  // Loading state management for showing spinners during translation loading
  private loadingSubject = new BehaviorSubject<boolean>(true); // Start as loading
  public isLoadingTranslations$ = this.loadingSubject.asObservable();

  public readonly availableLanguages: LanguageOption[] = [
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl', locale: 'ar-TN' },
    { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr', locale: 'en-US' },
    { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr', locale: 'fr-FR' }
  ];

  constructor(
    private translate: TranslateService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Initialize with stored language or default to English
    const storedLang = this.getStoredLanguage();
    this.currentLanguageSubject = new BehaviorSubject<Language>(storedLang);
    this.currentLanguage$ = this.currentLanguageSubject.asObservable();

    // Configure translate service
    this.translate.addLangs(this.availableLanguages.map(lang => lang.code));
    this.translate.setDefaultLang('en');

    // Load initial language (loading state already set to true in initialization)
    this.setLanguage(storedLang);
  }

  /**
   * Get the currently active language
   */
  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get the current language details
   */
  getCurrentLanguageOption(): LanguageOption {
    const currentLang = this.getCurrentLanguage();
    return this.availableLanguages.find(lang => lang.code === currentLang) || this.availableLanguages[0];
  }

  /**
   * Set the active language
   * Shows loading state while translations are being loaded
   */
  setLanguage(lang: Language): void {
    if (!this.availableLanguages.find(l => l.code === lang)) {
      console.warn(`Language '${lang}' not available. Falling back to default.`);
      lang = 'en';
    }

    // Show loading state
    this.loadingSubject.next(true);
    console.log(`[LanguageService] Loading translations for: ${lang}`);

    // Load translations asynchronously
    this.translate.use(lang).subscribe({
      next: () => {
        console.log(`[LanguageService] Translations loaded successfully for: ${lang}`);
        this.currentLanguageSubject.next(lang);
        this.storeLanguage(lang);
        this.updateDocumentDirection(lang);
        this.updateDocumentLang(lang);

        // Hide loading state after translations are loaded
        this.loadingSubject.next(false);
      },
      error: (error) => {
        console.error(`[LanguageService] Failed to load translations for ${lang}:`, error);
        // Hide loading state even on error
        this.loadingSubject.next(false);
      }
    });
  }

  /**
   * Switch to the next available language
   */
  switchLanguage(): void {
    const currentIndex = this.availableLanguages.findIndex(
      lang => lang.code === this.getCurrentLanguage()
    );
    const nextIndex = (currentIndex + 1) % this.availableLanguages.length;
    this.setLanguage(this.availableLanguages[nextIndex].code);
  }

  /**
   * Get translation for a key
   */
  instant(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }

  /**
   * Get translation as observable
   */
  get(key: string, params?: any): Observable<string> {
    return this.translate.get(key, params);
  }

  /**
   * Check if current language is RTL
   */
  isRTL(): boolean {
    return this.getCurrentLanguageOption().direction === 'rtl';
  }

  /**
   * Get current text direction
   */
  getDirection(): 'ltr' | 'rtl' {
    return this.getCurrentLanguageOption().direction;
  }

  /**
   * Get current locale for date/number formatting
   */
  getLocale(): string {
    return this.getCurrentLanguageOption().locale;
  }

  /**
   * Store language preference in localStorage
   */
  private storeLanguage(lang: Language): void {
    if (!this.isBrowser) return;

    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch (error) {
      console.error('Failed to store language preference:', error);
    }
  }

  /**
   * Get stored language preference from localStorage
   */
  private getStoredLanguage(): Language {
    if (!this.isBrowser) {
      return 'en'; // Default to English on server
    }

    try {
      const storedLang = localStorage.getItem(this.STORAGE_KEY) as Language;
      if (storedLang && this.availableLanguages.find(l => l.code === storedLang)) {
        return storedLang;
      }
    } catch (error) {
      console.error('Failed to retrieve language preference:', error);
    }
    return 'en'; // Default to English
  }

  /**
   * Update document direction attribute
   */
  private updateDocumentDirection(lang: Language): void {
    if (!this.isBrowser) return;

    const direction = this.availableLanguages.find(l => l.code === lang)?.direction || 'ltr';
    document.documentElement.setAttribute('dir', direction);
    document.body.setAttribute('dir', direction);
  }

  /**
   * Update document lang attribute
   */
  private updateDocumentLang(lang: Language): void {
    if (!this.isBrowser) return;

    document.documentElement.setAttribute('lang', lang);
  }
}
